import { Checkbox, FormControlLabel, MenuItem, TextField } from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import type { ReactNode } from 'react';
import type { AppColumn, AppScalarValue } from './AppTable.types';

export interface DefaultAppEditorProps<TRow> {
    column: AppColumn<TRow>;
    row: TRow;
    value: AppScalarValue;
    onChange(value: AppScalarValue): void;
    autoFocus: boolean;
    disabled: boolean;
    setEditorOpen(open: boolean): void;
    onAccept?(): void;
}

function toDateTime(value: AppScalarValue): DateTime | null {
    if (value instanceof Date) {
        const parsed = DateTime.fromJSDate(value);

        return parsed.isValid ? parsed : null;
    }

    if (typeof value === 'string' && value) {
        const parsed = DateTime.fromISO(value, {
            setZone: true,
        });

        return parsed.isValid ? parsed : null;
    }

    return null;
}

export function serializeAppDate(
    value: DateTime,
    column: Pick<AppColumn<unknown>, 'scalarType' | 'dateSerialization'>,
): string | undefined {
    if (!value.isValid) {
        return undefined;
    }

    if (column.scalarType === 'date' && column.dateSerialization !== 'localDateTime') {
        return value.toISODate() ?? undefined;
    }

    return (
        value.toISO({
            includeOffset: false,
        }) ?? undefined
    );
}

export function emptyAppNumberValue(value: number | null | undefined): null | undefined {
    return value === null ? null : undefined;
}

export function DefaultAppEditor<TRow>({
    column,
    row,
    value,
    onChange,
    autoFocus,
    disabled,
    setEditorOpen,
    onAccept,
}: DefaultAppEditorProps<TRow>): ReactNode {
    if (column.scalarType === 'number') {
        return (
            <TextField
                autoFocus={autoFocus}
                disabled={disabled}
                type="number"
                size="small"
                fullWidth
                slotProps={{
                    htmlInput: {
                        'aria-label': `Edit ${column.label}`,
                    },
                }}
                value={value === null || value === undefined ? '' : value}
                onChange={(event) => {
                    const text = event.target.value;

                    if (text === '') {
                        onChange(
                            emptyAppNumberValue(
                                typeof value === 'number' || value === null || value === undefined ? value : undefined,
                            ),
                        );
                    } else {
                        const number = Number(text);

                        if (Number.isFinite(number)) {
                            onChange(number);
                        }
                    }
                }}
            />
        );
    }

    if (column.scalarType === 'lookup' && column.lookup?.options) {
        const options = [...column.lookup.options];
        const selectedIndex = options.findIndex((option) => Object.is(option.value, value));

        if (selectedIndex < 0 && value !== null && value !== undefined) {
            options.unshift({
                value,
                label: column.lookup.labelFor?.(value, row) ?? String(value),
            });
        }

        const index = options.findIndex((option) => Object.is(option.value, value));

        return (
            <TextField
                select
                autoFocus={autoFocus}
                disabled={disabled}
                size="small"
                fullWidth
                slotProps={{
                    htmlInput: {
                        'aria-label': `Edit ${column.label}`,
                    },
                }}
                SelectProps={{
                    onOpen: () => setEditorOpen(true),
                    onClose: () => setEditorOpen(false),
                }}
                value={index < 0 ? '' : String(index)}
                onChange={(event) => {
                    const option = options[Number(event.target.value)];

                    if (option) {
                        onChange(option.value);
                        globalThis.setTimeout(() => onAccept?.(), 0);
                    }
                }}
            >
                {options.map((option, optionIndex) => (
                    <MenuItem key={`${optionIndex}`} value={String(optionIndex)}>
                        {option.label}
                    </MenuItem>
                ))}
            </TextField>
        );
    }

    if (column.scalarType === 'boolean') {
        return (
            <FormControlLabel
                label={column.label}
                control={
                    <Checkbox
                        autoFocus={autoFocus}
                        disabled={disabled}
                        checked={value === true}
                        onChange={(event) => onChange(event.target.checked)}
                    />
                }
            />
        );
    }

    if (column.scalarType === 'date' || column.scalarType === 'dateTime') {
        const pickerValue = toDateTime(value);
        const props = {
            autoFocus,
            disabled,
            value: pickerValue,
            onOpen: () => setEditorOpen(true),
            onClose: () => setEditorOpen(false),
            onAccept: () => onAccept?.(),
            onChange: (nextValue: DateTime | null) => {
                if (nextValue === null) {
                    onChange(value === null ? null : undefined);
                } else if (nextValue.isValid) {
                    onChange(serializeAppDate(nextValue, column));
                }
            },
            slotProps: {
                textField: {
                    size: 'small' as const,
                    fullWidth: true,
                    label: column.label,
                    slotProps: {
                        htmlInput: {
                            'aria-label': `Edit ${column.label}`,
                        },
                    },
                },
                actionBar: {
                    actions: ['clear', 'accept'] as ('clear' | 'accept')[],
                },
            },
        };

        return column.scalarType === 'date' ? (
            <DatePicker {...props} format="dd/MM/yyyy" />
        ) : (
            <DateTimePicker {...props} format="dd/MM/yyyy HH:mm" />
        );
    }

    return (
        <TextField
            autoFocus={autoFocus}
            disabled={disabled}
            size="small"
            fullWidth
            slotProps={{
                htmlInput: {
                    'aria-label': `Edit ${column.label}`,
                },
            }}
            value={value === null || value === undefined ? '' : String(value)}
            onChange={(event) => onChange(event.target.value)}
        />
    );
}
